/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import {
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { ActionIcon, NativeInput, Text } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';

/** Longest display filename accepted when renaming. */
const FILENAME_MAX = 200;

/** Minimal document shape the row needs — property/client documents both satisfy this. */
export interface DocumentRowDocument {
  document_id: string;
  filename: string;
  upload_date: string;
  file_size?: number;
  /** Link that forces a save — always an attachment. */
  download_url?: string;
  /** Link that renders in place; falls back to `download_url` when absent. */
  view_url?: string;
  /** Stored MIME type (older entries carry a human label instead). */
  type?: string;
}

export interface DocumentRowProps<TDocument extends DocumentRowDocument> {
  doc: TDocument;
  onRename: (documentId: string, filename: string) => void;
  onDelete: (documentId: string) => void;
  /** i18n namespace to resolve labels from (e.g. 'features/properties', 'features/clients'). */
  i18nNamespace: string;
  /** Key prefix within that namespace (e.g. 'detailView.documents', 'profile.documents'). */
  i18nKeyPrefix: string;
  /** Opens the in-app viewer; the row name is the target. */
  onOpen?: (doc: TDocument) => void;
}

/**
 * Formats a byte count into a human-readable file size (B, KB or MB).
 *
 * @param bytes - Raw file size in bytes
 * @returns Formatted size, or empty string when absent
 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * One link-shaped action: a real anchor, so the browser -- not a script --
 * decides between opening a tab and saving the file. Rendered inert when
 * the link is missing, which is what a still-uploading document looks like.
 *
 * @param props - Target URL, accessible label and the icon to show.
 * @returns The rendered action.
 */
const LinkAction: React.FC<{
  href?: string;
  label: string;
  newTab?: boolean;
  filename?: string;
  children: React.ReactNode;
}> = ({ href, label, newTab, filename, children }) => {
  if (!href) {
    return (
      <ActionIcon
        type="button"
        aria-label={label}
        title={label}
        disabled
        size={28}
        className="text-(--text-secondary)"
      >
        {children}
      </ActionIcon>
    );
  }

  return (
    <ActionIcon
      component="a"
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      download={filename}
      aria-label={label}
      title={label}
      size={28}
      className="text-(--text-secondary) hover:text-(--seed-base)"
    >
      {children}
    </ActionIcon>
  );
};

/**
 * Document row with rename, delete and open actions, revealed on hover or
 * keyboard focus. Shared by the property and client document tabs — they
 * only differ in the i18n namespace/key-prefix used to label the actions.
 *
 * @param props - The document, rename/delete handlers, and i18n location.
 * @returns The rendered document row.
 */
export const DocumentRow = <TDocument extends DocumentRowDocument>({
  doc,
  onRename,
  onDelete,
  i18nNamespace,
  i18nKeyPrefix,
  onOpen,
}: DocumentRowProps<TDocument>) => {
  const { t } = useTranslation(i18nNamespace);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(doc.filename);
  const meta = [formatFileSize(doc.file_size), formatLocalizedDate(doc.upload_date)]
    .filter(Boolean)
    .join(' · ');

  const saveName = () => {
    const next = draft.trim();
    if (next && next !== doc.filename) onRename(doc.document_id, next);
    setEditing(false);
  };

  return (
    <div className="group flex w-full items-center gap-(--space-2) px-(--space-3) py-(--space-2) rounded-(--radius-md) border border-(--border-ultra-subtle) bg-(--surface-primary) hover:border-(--border-subtle) hover:shadow-[var(--shadow-sm)] transition-[var(--transition-standard)]">
      <FileTextIcon width={14} height={14} className="text-(--seed-base) shrink-0" />
      {editing ? (
        <NativeInput
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={saveName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') saveName();
            if (event.key === 'Escape') setEditing(false);
          }}
          autoFocus
          maxLength={FILENAME_MAX}
          aria-label={t(`${i18nKeyPrefix}.nameLabel`)}
          className="min-w-0 flex-1 px-(--space-2) py-(--space-1) rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-primary) text-[length:var(--text-body-sm)] text-(--text-primary) [font-family:var(--font-family)]"
        />
      ) : onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(doc)}
          title={t(`${i18nKeyPrefix}.view`)}
          className="min-w-0 flex-1 truncate border-0 bg-transparent p-0 text-left text-[length:var(--text-body-sm)] text-(--text-primary) cursor-pointer [font-family:var(--font-family)] hover:underline"
        >
          {doc.filename}
        </button>
      ) : (
        <Text as="span" className="text-[length:var(--text-body-sm)] text-(--text-primary) flex-1 truncate">
          {doc.filename}
        </Text>
      )}
      {meta && (
        <Text as="span" className="text-[length:var(--text-micro)] text-(--text-tertiary) shrink-0">
          {meta}
        </Text>
      )}
      <div className="flex shrink-0 items-center gap-(--space-1) opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        <LinkAction
          href={doc.view_url || doc.download_url}
          label={t(`${i18nKeyPrefix}.openInNewTab`)}
          newTab
        >
          <ExternalLinkIcon width={14} height={14} />
        </LinkAction>
        <LinkAction
          href={doc.download_url}
          label={t(`${i18nKeyPrefix}.download`)}
          filename={doc.filename}
        >
          <DownloadIcon width={14} height={14} />
        </LinkAction>
        <ActionIcon
          type="button"
          aria-label={t(`${i18nKeyPrefix}.rename`)}
          title={t(`${i18nKeyPrefix}.rename`)}
          onClick={() => setEditing(true)}
          size={28}
          className="text-(--text-secondary) hover:text-(--seed-base)"
        >
          <Pencil1Icon width={14} height={14} />
        </ActionIcon>
        <ActionIcon
          type="button"
          aria-label={t(`${i18nKeyPrefix}.remove`)}
          title={t(`${i18nKeyPrefix}.remove`)}
          onClick={() => onDelete(doc.document_id)}
          size={28}
          className="text-(--text-secondary) hover:text-(--status-error)"
        >
          <TrashIcon width={14} height={14} />
        </ActionIcon>
      </div>
    </div>
  );
};

export default DocumentRow;
